// FundManager.tsx — "Fonds verwalten" (Daten view): a list of the current funds with a
// delete button on user-added ones only, plus "Eigenen Fonds per ISIN hinzufügen"
// (ISIN -> Suchen -> confirm -> Hinzufügen). All transition/validation logic lives in
// lib/fundAdmin.ts (framework-free, unit-tested) — this component is a thin renderer over
// it, matching the "no heavy rendering tests" convention this package already follows
// (vite.config.ts runs tests in a Node environment, no DOM).
import { useState } from 'preact/hooks';
import { deleteFund as apiDeleteFund, getFundLookup, patchFundEnabled, patchFundTer, postCreateFund } from '../lib/api.js';
import {
  buildCreateFundBody,
  canSubmit,
  currencyWarning,
  describeApiError,
  initialConfirmForm,
  isValidIsinFormat,
  needsManualFundFields,
  normalizeIsin,
} from '../lib/fundAdmin.js';
import type { ConfirmForm } from '../lib/fundAdmin.js';
import type { FundInfo, FundLookupResponse } from '../lib/types.js';
import { ToggleField } from './ToggleField.js';
import { Hint, useExplain } from './Hint.js';

type AddState =
  | { status: 'idle' }
  | { status: 'searching' }
  | { status: 'lookup-error'; error: string }
  | { status: 'confirm'; lookup: FundLookupResponse; form: ConfirmForm }
  | { status: 'adding'; lookup: FundLookupResponse; form: ConfirmForm }
  | { status: 'add-error'; lookup: FundLookupResponse; form: ConfirmForm; error: string };

export interface FundManagerProps {
  funds: FundInfo[];
  /** Re-fetches the fund/health lists after a successful add or delete. */
  onChanged: () => void;
}

export function FundManager({ funds, onChanged }: FundManagerProps) {
  const [isinInput, setIsinInput] = useState('');
  const [addState, setAddState] = useState<AddState>({ status: 'idle' });
  const [deleteConfirmIsin, setDeleteConfirmIsin] = useState<string | null>(null);
  const [deletingIsin, setDeletingIsin] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [togglingIsin, setTogglingIsin] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [terEditIsin, setTerEditIsin] = useState<string | null>(null);
  const [terInputValue, setTerInputValue] = useState('');
  const [terSavingIsin, setTerSavingIsin] = useState<string | null>(null);
  const [terError, setTerError] = useState<string | null>(null);

  const busy = addState.status === 'searching' || addState.status === 'adding';

  async function handleSearch(e: Event) {
    e.preventDefault();
    const isin = normalizeIsin(isinInput);
    if (!isValidIsinFormat(isin)) {
      setAddState({ status: 'lookup-error', error: `Ungültiges ISIN-Format: ${isin} (2 Buchstaben + 10 Zeichen erwartet).` });
      return;
    }
    setAddState({ status: 'searching' });
    try {
      const lookup = await getFundLookup(isin);
      setAddState({ status: 'confirm', lookup, form: initialConfirmForm(lookup) });
    } catch (err) {
      setAddState({ status: 'lookup-error', error: describeApiError(err) });
    }
  }

  function patchForm(patch: Partial<ConfirmForm>) {
    setAddState((s) =>
      s.status === 'confirm' || s.status === 'add-error'
        ? { status: 'confirm', lookup: s.lookup, form: { ...s.form, ...patch } }
        : s,
    );
  }

  async function handleAdd() {
    if (addState.status !== 'confirm' && addState.status !== 'add-error') return;
    const { lookup, form } = addState;
    setAddState({ status: 'adding', lookup, form });
    try {
      await postCreateFund(buildCreateFundBody(lookup.isin, lookup, form));
      setIsinInput('');
      setAddState({ status: 'idle' });
      onChanged();
    } catch (err) {
      setAddState({ status: 'add-error', lookup, form, error: describeApiError(err) });
    }
  }

  function cancelAdd() {
    setAddState({ status: 'idle' });
    setIsinInput('');
  }

  async function handleDelete(isin: string) {
    setDeletingIsin(isin);
    setDeleteError(null);
    try {
      await apiDeleteFund(isin);
      setDeleteConfirmIsin(null);
      onChanged();
    } catch (err) {
      setDeleteError(describeApiError(err));
    } finally {
      setDeletingIsin(null);
    }
  }

  async function handleToggle(isin: string, enabled: boolean) {
    setTogglingIsin(isin);
    setToggleError(null);
    try {
      await patchFundEnabled(isin, enabled);
      onChanged();
    } catch (err) {
      setToggleError(describeApiError(err));
    } finally {
      setTogglingIsin(null);
    }
  }

  function startTerEdit(fund: FundInfo) {
    setTerEditIsin(fund.isin);
    setTerInputValue(fund.terBp !== null ? String(fund.terBp / 100) : '');
    setTerError(null);
  }

  function cancelTerEdit() {
    setTerEditIsin(null);
    setTerError(null);
  }

  /** Empty input clears the TER (terBp: null); otherwise %->bp, rounded, validated
   * against the same 0..5 % range PATCH /api/funds/:isin enforces server-side. */
  async function saveTer(isin: string) {
    const trimmed = terInputValue.trim();
    const terBp = trimmed === '' ? null : Math.round(Number(trimmed) * 100);
    if (terBp !== null && (!Number.isFinite(terBp) || terBp < 0 || terBp > 500)) {
      setTerError('TER muss zwischen 0 % und 5 % liegen.');
      return;
    }
    setTerSavingIsin(isin);
    setTerError(null);
    try {
      await patchFundTer(isin, terBp);
      setTerEditIsin(null);
      onChanged();
    } catch (err) {
      setTerError(describeApiError(err));
    } finally {
      setTerSavingIsin(null);
    }
  }

  const confirmState =
    addState.status === 'confirm' || addState.status === 'adding' || addState.status === 'add-error'
      ? addState
      : null;

  return (
    <div class="card">
      <h2>Fondsverwaltung</h2>
      <Hint>
        <p class="field-hint">
          Fonds aktivieren/deaktivieren, hinzufügen oder entfernen. Nur aktivierte Fonds
          erscheinen in der Fonds-Auswahl im Cockpit.
        </p>
      </Hint>

      {funds.length === 0 ? (
        <div class="empty-state">Noch keine Fonds hinterlegt.</div>
      ) : (
        <ul class="source-list">
          {funds.map((f) => (
            <li key={f.isin} class={`source-row fund-row${f.enabled ? '' : ' fund-row-disabled'}`}>
              <span>
                {f.name} <span class="faint">({f.isin})</span>
                {!f.enabled ? <span class="faint"> · deaktiviert</span> : null}
                <br />
                <span class="faint">
                  Kurse: {f.coverage.priceCount} · Ausschüttungen: {f.coverage.distributionCount} · Meldungen:{' '}
                  {f.coverage.meldungCount}
                </span>
                <br />
                {terEditIsin === f.isin ? (
                  <span class="btn-row" style={{ marginTop: '0.3rem' }}>
                    <input
                      type="number"
                      class="num-input"
                      min={0}
                      max={5}
                      step={0.01}
                      inputMode="decimal"
                      value={terInputValue}
                      onInput={(e) => setTerInputValue((e.currentTarget as HTMLInputElement).value)}
                      aria-label={`TER für ${f.name} in Prozent`}
                      style={{ width: '4.5rem' }}
                    />
                    <button
                      type="button"
                      class="btn btn-primary"
                      disabled={terSavingIsin === f.isin}
                      onClick={() => void saveTer(f.isin)}
                    >
                      {terSavingIsin === f.isin ? <span class="spinner" /> : 'Speichern'}
                    </button>
                    <button type="button" class="btn" disabled={terSavingIsin === f.isin} onClick={cancelTerEdit}>
                      Abbrechen
                    </button>
                  </span>
                ) : (
                  <span class="faint">
                    TER: {f.terBp !== null ? `${(f.terBp / 100).toFixed(2)} %` : '—'}{' '}
                    <button type="button" class="btn" onClick={() => startTerEdit(f)}>
                      Bearbeiten
                    </button>
                  </span>
                )}
              </span>
              <span class="btn-row">
                <button
                  type="button"
                  class="switch"
                  role="switch"
                  aria-checked={f.enabled}
                  aria-label={`${f.name} ${f.enabled ? 'deaktivieren' : 'aktivieren'}`}
                  disabled={togglingIsin === f.isin}
                  onClick={() => void handleToggle(f.isin, !f.enabled)}
                >
                  <span class="track">
                    <span class="knob" />
                  </span>
                </button>
                {f.seeded ? (
                  <span class="faint">Basis-Fonds</span>
                ) : deleteConfirmIsin === f.isin ? (
                  <span class="btn-row">
                    <button
                      type="button"
                      class="btn"
                      disabled={deletingIsin === f.isin}
                      onClick={() => void handleDelete(f.isin)}
                    >
                      {deletingIsin === f.isin ? <span class="spinner" /> : 'Wirklich löschen'}
                    </button>
                    <button type="button" class="btn" onClick={() => setDeleteConfirmIsin(null)}>
                      Abbrechen
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    class="btn"
                    onClick={() => {
                      setDeleteConfirmIsin(f.isin);
                      setDeleteError(null);
                    }}
                  >
                    Entfernen
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {toggleError ? (
        <div class="banner banner-error" role="alert" style={{ marginTop: '0.6rem' }}>
          <strong>Ändern fehlgeschlagen</strong>
          <span>{toggleError}</span>
        </div>
      ) : null}

      {terError ? (
        <div class="banner banner-error" role="alert" style={{ marginTop: '0.6rem' }}>
          <strong>TER konnte nicht gespeichert werden</strong>
          <span>{terError}</span>
        </div>
      ) : null}

      {deleteError ? (
        <div class="banner banner-error" role="alert" style={{ marginTop: '0.6rem' }}>
          <strong>Löschen fehlgeschlagen</strong>
          <span>{deleteError}</span>
        </div>
      ) : null}

      <div class="section-title">Eigenen Fonds per ISIN hinzufügen</div>

      <form onSubmit={(e) => void handleSearch(e)}>
        <div class="field">
          <label for="add-fund-isin">ISIN</label>
          <input
            id="add-fund-isin"
            type="text"
            value={isinInput}
            onInput={(e) => setIsinInput((e.currentTarget as HTMLInputElement).value)}
            placeholder="z. B. IE00B5BMR087"
            disabled={busy}
          />
        </div>
        <button type="submit" class="btn btn-primary btn-block" disabled={busy || isinInput.trim() === ''}>
          {addState.status === 'searching' ? <span class="spinner" /> : 'Suchen'}
        </button>
      </form>

      {addState.status === 'lookup-error' ? (
        <div class="banner banner-error" role="alert" style={{ marginTop: '0.6rem' }}>
          <strong>Suche fehlgeschlagen</strong>
          <span>{addState.error}</span>
        </div>
      ) : null}

      {confirmState ? (
        <ConfirmPanel
          lookup={confirmState.lookup}
          form={confirmState.form}
          busy={confirmState.status === 'adding'}
          error={confirmState.status === 'add-error' ? confirmState.error : null}
          onChange={patchForm}
          onSubmit={() => void handleAdd()}
          onCancel={cancelAdd}
        />
      ) : null}
    </div>
  );
}

interface ConfirmPanelProps {
  lookup: FundLookupResponse;
  form: ConfirmForm;
  busy: boolean;
  error: string | null;
  onChange: (patch: Partial<ConfirmForm>) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

function ConfirmPanel({ lookup, form, busy, error, onChange, onSubmit, onCancel }: ConfirmPanelProps) {
  const showExplanations = useExplain();
  const warning = currencyWarning(lookup, form);
  const canAdd = canSubmit(lookup, form) && !busy;
  const manualEntry = form.useManual || lookup.yahooCandidates.length === 0;

  return (
    <div class="card" style={{ marginTop: '0.75rem' }}>
      <h3>{lookup.isin}</h3>

      {lookup.oekb ? (
        <dl class="kv-list">
          <dt>Name (OeKB)</dt>
          <dd style={{ margin: 0 }}>{lookup.oekb.name}</dd>
          <dt>Emittent</dt>
          <dd style={{ margin: 0 }}>{lookup.oekb.issuer}</dd>
          <dt>Fondswährung</dt>
          <dd style={{ margin: 0 }}>{lookup.oekb.currency}</dd>
          <dt>Meldefonds</dt>
          <dd style={{ margin: 0 }}>{lookup.oekb.meldefonds ? 'Ja' : 'Nein'}</dd>
          <dt>Art</dt>
          <dd style={{ margin: 0 }}>
            {lookup.oekb.kind === 'acc' ? 'Thesaurierend' : lookup.oekb.kind === 'dist' ? 'Ausschüttend' : 'Unbekannt (bitte unten angeben)'}
          </dd>
        </dl>
      ) : (
        <div class="banner banner-warning">
          <strong>Kein OeKB-Meldefonds gefunden</strong>
          <span>
            {lookup.oekbError
              ? `OeKB-Abfrage fehlgeschlagen: ${lookup.oekbError} — bitte erneut versuchen.`
              : 'Für diese ISIN liegen keine OeKB-Steuerdaten vor (kein KESt-Meldefonds oder unbekannt). Bitte Name, Fondswährung und Art unten manuell angeben.'}
          </span>
        </div>
      )}

      <div class="section-title">Yahoo-Ticker</div>
      {lookup.yahooCandidates.length > 0 ? (
        <div class="field">
          <div class="segmented segmented-scroll" role="group" aria-label="Yahoo-Symbol">
            {lookup.yahooCandidates.map((c) => (
              <button
                key={c.symbol}
                type="button"
                class="chip"
                aria-pressed={!form.useManual && form.selectedSymbol === c.symbol}
                onClick={() => onChange({ useManual: false, selectedSymbol: c.symbol })}
              >
                {c.symbol} {c.currency ? `(${c.currency})` : '(Währung unbekannt)'}
              </button>
            ))}
            <button type="button" class="chip" aria-pressed={form.useManual} onClick={() => onChange({ useManual: true })}>
              Manuell
            </button>
          </div>
          {!form.useManual && form.selectedSymbol ? (
            <p class="field-hint">
              {lookup.yahooCandidates.find((c) => c.symbol === form.selectedSymbol)?.name ?? ''}
              {' · '}
              {lookup.yahooCandidates.find((c) => c.symbol === form.selectedSymbol)?.exchange ?? ''}
            </p>
          ) : null}
        </div>
      ) : (
        <p class="field-hint">
          Keine Yahoo-Vorschläge gefunden{lookup.yahooError ? ` (${lookup.yahooError})` : ''} — bitte Symbol manuell eingeben.
        </p>
      )}

      {manualEntry ? (
        <div class="field">
          <label for="add-fund-manual-symbol">Yahoo-Symbol</label>
          <input
            id="add-fund-manual-symbol"
            type="text"
            value={form.manualSymbol}
            onInput={(e) => onChange({ manualSymbol: (e.currentTarget as HTMLInputElement).value })}
            placeholder="z. B. SXR8.DE"
          />
          {showExplanations ? (
            <div class="field-hint">Wird beim Hinzufügen live geprüft (muss in EUR notieren).</div>
          ) : null}
        </div>
      ) : null}

      {warning ? (
        <div class="banner banner-warning" role="alert">
          <strong>Falsche Währung</strong>
          <span>{warning}</span>
        </div>
      ) : null}

      {needsManualFundFields(lookup) ? (
        <>
          <div class="field">
            <label for="add-fund-name">Name</label>
            <input
              id="add-fund-name"
              type="text"
              value={form.name}
              onInput={(e) => onChange({ name: (e.currentTarget as HTMLInputElement).value })}
            />
          </div>
          <div class="field">
            <label for="add-fund-currency">Fondswährung</label>
            <input
              id="add-fund-currency"
              type="text"
              value={form.currency}
              onInput={(e) => onChange({ currency: (e.currentTarget as HTMLInputElement).value })}
              placeholder="z. B. EUR"
            />
          </div>
          <div class="field">
            <span class="field-label">Art</span>
            <div class="segmented" role="group" aria-label="Art">
              <button type="button" class="chip" aria-pressed={form.kind === 'acc'} onClick={() => onChange({ kind: 'acc' })}>
                Thesaurierend
              </button>
              <button type="button" class="chip" aria-pressed={form.kind === 'dist'} onClick={() => onChange({ kind: 'dist' })}>
                Ausschüttend
              </button>
            </div>
          </div>
          <ToggleField label="Meldefonds" checked={form.meldefonds} onChange={(v) => onChange({ meldefonds: v })} />
        </>
      ) : null}

      <div class="field">
        <label for="add-fund-ter">TER in Basispunkten (optional)</label>
        <input
          id="add-fund-ter"
          type="number"
          min="0"
          max="10000"
          value={form.terBp}
          onInput={(e) => onChange({ terBp: (e.currentTarget as HTMLInputElement).value })}
        />
      </div>

      {error ? (
        <div class="banner banner-error" role="alert">
          <strong>Hinzufügen fehlgeschlagen</strong>
          <span>{error}</span>
        </div>
      ) : null}

      <div class="btn-row">
        <button type="button" class="btn btn-primary" disabled={!canAdd} onClick={onSubmit}>
          {busy ? <span class="spinner" /> : 'Hinzufügen'}
        </button>
        <button type="button" class="btn" onClick={onCancel} disabled={busy}>
          Abbrechen
        </button>
      </div>
    </div>
  );
}
